import type { WeeklyPeriod } from "@swivl-mono/types";

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

/**
 * Render `WeeklyPeriod[]` as a compact human-readable string like
 *   "Mon-Fri: 9:00 AM - 5:30 PM, Sat: 9:00 AM - 4:00 PM, Sun: closed"
 *   "Daily: 6:00 AM - 10:00 PM"
 *   "Closed"
 *
 * Used by the bot's `load_location` tool to surface structured Schedule
 * rows in a form the LLM can quote verbatim. Inverse of `parseFreeformHours`.
 *
 * Days with the same period set are grouped into ranges (Mon-Fri); split
 * shifts on the same day render as comma-joined ("9:00 AM - 12:00 PM,
 * 1:00 PM - 5:00 PM"). Closed days appear at the end as "Sun: closed".
 */
export function formatWeeklyPeriods(periods: WeeklyPeriod[] | null | undefined): string {
  if (!periods || periods.length === 0) return "Closed";

  // Build the per-day signature: a sorted list of "open-close" strings.
  const signatures: (string | null)[] = new Array(7).fill(null);
  for (let dow = 0; dow < 7; dow++) {
    const dayPeriods = periods.filter((p) => p.dayOfWeek === dow).sort((a, b) => a.openMinute - b.openMinute);
    if (dayPeriods.length === 0) continue;
    signatures[dow] = dayPeriods.map((p) => `${formatMinute(p.openMinute)} - ${formatMinute(p.closeMinute)}`).join(", ");
  }

  // Walk Mon..Sun (1..6, then 0) so the output reads in the conventional
  // weekday order. Group consecutive days with the same signature.
  const order: number[] = [1, 2, 3, 4, 5, 6, 0];
  const segments: { days: number[]; sig: string | null }[] = [];
  for (const dow of order) {
    const sig = signatures[dow];
    const last = segments[segments.length - 1];
    if (last && last.sig === sig && areConsecutive(last.days[last.days.length - 1], dow)) {
      last.days.push(dow);
    } else {
      segments.push({ days: [dow], sig });
    }
  }

  // If every day shares one signature, render as "Daily: ..."
  const allDaysCovered = segments.length === 1 && segments[0].days.length === 7;
  if (allDaysCovered && segments[0].sig) {
    return `Daily: ${segments[0].sig}`;
  }

  // Otherwise render each segment as "Mon-Fri: 9:00 AM - 5:30 PM" or "Sun: closed".
  return segments
    .map((s) => {
      const label = s.days.length === 1 ? DAY_LABELS[s.days[0]] : `${DAY_LABELS[s.days[0]]}-${DAY_LABELS[s.days[s.days.length - 1]]}`;
      return `${label}: ${s.sig ?? "closed"}`;
    })
    .join(", ");
}

function formatMinute(m: number): string {
  // Periods may exceed 1440 to express midnight-crossing close times.
  const wrapped = m % 1440;
  let h = Math.floor(wrapped / 60);
  const min = wrapped % 60;
  const period = h >= 12 ? "PM" : "AM";
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return min === 0 ? `${h}:00 ${period}` : `${h}:${String(min).padStart(2, "0")} ${period}`;
}

function areConsecutive(a: number, b: number): boolean {
  // Mon-Sun render order is 1,2,3,4,5,6,0 — Sat (6) → Sun (0) is consecutive.
  if (a === 6 && b === 0) return true;
  return b === a + 1;
}
